"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { CheckCircle2, UsersRound } from "lucide-react";
import { toggleCompanionDayIntent } from "@/app/actions/companion-day-intents";
import type { CompanionWeekIntentSummary } from "./companion-home-state";

type CompanionDayIntentControlsProps = {
  date: string;
  placeSlug: string | null;
  placeName: string | null;
  summary: CompanionWeekIntentSummary;
};

export function CompanionDayIntentControls({
  date,
  placeSlug,
  placeName,
  summary,
}: CompanionDayIntentControlsProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [localSummary, setLocalSummary] = useState(summary);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocalSummary(summary);
  }, [summary]);

  function handleToggle(item: CompanionWeekIntentSummary["items"][number]) {
    if (!placeSlug || !placeName || isPending) return;
    const nextActive = !item.active;
    const delta = nextActive ? 1 : -1;
    const previous = localSummary;

    setError(null);
    setLocalSummary({
      ...localSummary,
      items: localSummary.items.map((current) =>
        current.id === item.id
          ? {
              ...current,
              active: nextActive,
              count: Math.max(0, current.count + delta),
              countLabel: localCountLabel(current.id, Math.max(0, current.count + delta)),
            }
          : current,
      ),
    });

    startTransition(async () => {
      const result = await toggleCompanionDayIntent({
        date,
        placeSlug,
        placeName,
        intentType: item.id,
      });

      if (!result.ok) {
        setLocalSummary(previous);
        setError(result.error);
        return;
      }

      router.refresh();
    });
  }

  const disabled = !placeSlug || !placeName;

  return (
    <div className="mt-3 rounded-sm border border-border bg-white px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[1.1px] text-text-muted">
          {localSummary.title}
        </p>
        <UsersRound className="h-3.5 w-3.5 shrink-0 text-gold" aria-hidden="true" />
      </div>
      <div className="mt-2 grid gap-1.5">
        {localSummary.items.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={item.active}
            disabled={disabled || isPending}
            onClick={() => handleToggle(item)}
            className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-sm border px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              item.active
                ? "border-emerald-300 bg-emerald-50 text-emerald-950"
                : "border-border bg-warm-bg text-navy hover:border-gold/50 hover:bg-white"
            }`}
          >
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[12px] font-semibold leading-tight">
                {item.active ? (
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                ) : null}
                {item.active ? item.activeLabel : item.actionLabel}
              </span>
              {item.namesLabel ? (
                <span className="mt-0.5 block truncate text-[10.5px] font-semibold text-text-muted">
                  {item.namesLabel}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10.5px] font-bold leading-none text-navy">
              {item.countLabel}
            </span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-text-muted">
        {totalIntentCount(localSummary) === 0
          ? localSummary.emptyLabel
          : localSummary.privacyLabel}
        .
      </p>
      {error ? (
        <p className="mt-2 text-[11px] font-semibold leading-snug text-red-700" role="status">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function totalIntentCount(summary: CompanionWeekIntentSummary): number {
  return summary.items.reduce((total, item) => total + item.count, 0);
}

function localCountLabel(
  id: CompanionWeekIntentSummary["items"][number]["id"],
  count: number,
): string {
  const suffixes: Record<CompanionWeekIntentSummary["items"][number]["id"], string> = {
    ride: "riding",
    "day-off": "off",
    event: "going",
  };
  return `${count} ${suffixes[id]}`;
}
